/**
 * useAdminOrganizationsList Hook
 *
 * Fetches and manages organization list data for the admin audit dashboard
 */

import { useMemo } from 'react';

import { useGetAdminOrganizationsQuery } from '../../store/api';
import type { OrganizationSummary } from '../../types';

interface UseAdminOrganizationsListResult {
  /** List of organizations with audit data */
  organizations: OrganizationSummary[];
  /** Total number of organizations */
  total: number;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Whether list is empty */
  isEmpty: boolean;
}

export const useAdminOrganizationsList = (): UseAdminOrganizationsListResult => {
  const { data, isLoading, error, refetch: refetchQuery } = useGetAdminOrganizationsQuery();

  const refetch = (): void => {
    void refetchQuery();
  };

  const organizations = useMemo(() => data?.organizations || [], [data]);
  const total = data?.total || organizations.length;
  const isEmpty = !isLoading && organizations.length === 0;

  return {
    organizations,
    total,
    isLoading,
    error: error ? 'Failed to load organizations list' : null,
    refetch,
    isEmpty,
  };
};

export default useAdminOrganizationsList;

